import { Alert } from '@mui/material';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Analytics } from '../data/Analytics';

// Proposal specifies 1080p
const ASPECT_RATIO = {
	target: 16 / 9,
	tolerance: 0.03,
} as const;
const MIN_SIZE = { width: 1920, height: 1080 } as const;

type DisplayInfo = {
	width: number;
	height: number;
	aspectRatio: number;
	isFullScreen: boolean;
	meetsRequirements: boolean;
	sufficientSize: boolean;
	sufficientAspectRatio: boolean;
};

/** Warns when the current browser window does not meet the recommended game display requirements. */
export const DisplayCompatibilityWarning: FunctionComponent = () => {
	const [info, setInfo] = useState<DisplayInfo | undefined>(evaluateDisplay);
	const hasTrackedOnLoad = useRef(false);

	useEffect(function trackOnLoad() {
		if (info && !hasTrackedOnLoad.current) {
			Analytics.track('display_compatibility', {
				width: info.width,
				height: info.height,
				aspect_ratio: Number(info.aspectRatio.toFixed(4)),
				is_fullscreen: info.isFullScreen,
				meets_requirements: info.meetsRequirements,
			});
			hasTrackedOnLoad.current = true;
		}
	}, [info]);

	useEffect(function setupListeners() {
		function onChange() {
			setInfo(evaluateDisplay());
		}

		window.addEventListener('resize', onChange);
		document.addEventListener('fullscreenchange', onChange);

		return () => {
			window.removeEventListener('resize', onChange);
			document.removeEventListener('fullscreenchange', onChange);
		};
	}, []);

	const warnings: string[] = [];
	if (info) {
		if (!info.isFullScreen) {
			warnings.push('A full screen browser window. Maximise the window before starting.');
		}
		if (!info.sufficientSize) {
			warnings.push(`A window of at least ${MIN_SIZE.width}x${MIN_SIZE.height} pixels.`
				+ ` Currently ${info.width}x${info.height}.`);
		}
		if (!info.sufficientAspectRatio) {
			warnings.push('An aspect ratio (screen width / height) of 16:9.'
				+ ` Increase the ${(info.aspectRatio < ASPECT_RATIO.target) ? 'width' : 'height'} of the window before starting.`);
		}
	}

	return (!info || info.meetsRequirements)
		? null
		: <Alert severity='warning' sx={{ marginTop: 2 }}>
			The current browser window will not provide an optimal experience. The game is designed for:
			<ul>
				{warnings.map((warning, index) => <li key={`warning${index}`}>{warning}</li>)}
			</ul>
		</Alert>;
};

function evaluateDisplay(): DisplayInfo | undefined {
	if ((typeof window === 'undefined') || (typeof document === 'undefined')) {
		return undefined;
	} else {
		const width = window.innerWidth;
		const height = window.innerHeight;
		const aspectRatio = (height > 0) ? (width / height) : 0;
		const isFullScreen = (height === screen.height) && (width === screen.width);
		const sufficientAspectRatio = isAspectRatioSupported(aspectRatio);
		const sufficientSize = (width >= MIN_SIZE.width) && (height >= MIN_SIZE.height);

		return {
			width, height, aspectRatio, isFullScreen, sufficientSize, sufficientAspectRatio,
			meetsRequirements: isFullScreen && sufficientAspectRatio && sufficientSize,
		};
	}
}

function isAspectRatioSupported(aspectRatio: number): boolean {
	return Math.abs(aspectRatio - ASPECT_RATIO.target) <= ASPECT_RATIO.tolerance;
}
